import { useEffect, useState } from "react";
import {
  RotateCcw,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  Search,
  Filter,
  Eye,
  Package,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Camera,
  FileText,
  Truck,
  User,
  Calendar,
  DollarSign,
  MessageSquare,
  X,
  Plus
} from "lucide-react";
import { Card } from "../../Components/Card";
import { Button } from "../../Components/Button";
import { Badge } from "../../Components/Badge";
import { Input } from "../../Components/Input";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../Components/Tabs";
import { Toaster as AppToaster } from "../../Components/Sonner";
import Pagination from "../../Components/Pagination";
import { toast } from "sonner";
import { decryptData } from '../../../utils/storageCrypto';
import {CreateRequestModal} from './RequestModal';

const mockReturns = [
  {
    id: "RET-2024-001",
    orderId: "ORD-1892",
    customerId: "CUST-441",
    customerName: "Meridian Logistics",
    customerEmail: "ops@meridianlogistics.com",
    product: "Industrial Conveyor Belt - 20m",
    sku: "CB-20M-IND",
    quantity: 2,
    unitPrice: 1840,
    totalValue: 3680,
    reason: "defective",
    reasonDetail: "Belt shows premature fraying after 3 days of use. Material quality is significantly below spec.",
    status: "pending_review",
    requestDate: "2024-06-23",
    lastUpdated: "2024-06-23",
    hasPhotos: true,
    priority: "high",
  },
  {
    id: "RET-2024-002",
    orderId: "ORD-1745",
    customerId: "CUST-228",
    customerName: "Apex Manufacturing",
    customerEmail: "purchasing@apexmfg.com",
    product: "Hydraulic Press - 50 Ton",
    sku: "HP-50T-PRO",
    quantity: 1,
    unitPrice: 12500,
    totalValue: 12500,
    reason: "damaged_shipping",
    reasonDetail: "Unit arrived with visible damage to the control panel and hydraulic lines were leaking on arrival.",
    status: "inspection",
    requestDate: "2024-06-20",
    lastUpdated: "2024-06-22",
    hasPhotos: true,
    inspectionNotes: "Damage confirmed. Control panel cracked, hydraulic seals compromised. Carrier liability claim initiated.",
    priority: "high",
  },
  {
    id: "RET-2024-003",
    orderId: "ORD-1801",
    customerId: "CUST-315",
    customerName: "Global Fabricators",
    customerEmail: "returns@globalfab.net",
    product: "CNC Milling Bits - Set of 50",
    sku: "CNC-MB-50SET",
    quantity: 3,
    unitPrice: 380,
    totalValue: 1140,
    reason: "wrong_item",
    reasonDetail: "Received metric bits but order specified imperial sizing. Cannot use for current project.",
    status: "approved",
    requestDate: "2024-06-21",
    lastUpdated: "2024-06-22",
    hasPhotos: false,
    refundAmount: 1140,
    priority: "medium",
  },
  {
    id: "RET-2024-004",
    orderId: "ORD-1655",
    customerId: "CUST-502",
    customerName: "Titan Steel Works",
    customerEmail: "admin@titansteel.com",
    product: "Welding Wire - 15kg Spool",
    sku: "WW-15KG-316L",
    quantity: 10,
    unitPrice: 145,
    totalValue: 1450,
    reason: "not_as_described",
    reasonDetail: "Material certification shows 304L grade but we ordered 316L. Chemical composition mismatch.",
    status: "pending_review",
    requestDate: "2024-06-24",
    lastUpdated: "2024-06-24",
    hasPhotos: false,
    priority: "high",
  },
  {
    id: "RET-2024-005",
    orderId: "ORD-1590",
    customerId: "CUST-187",
    customerName: "Pacific Assemblies",
    customerEmail: "ops@pacificassemblies.com",
    product: "Safety Valve - 2 inch BSP",
    sku: "SV-2BSP-SS",
    quantity: 25,
    unitPrice: 89,
    totalValue: 2225,
    reason: "defective",
    reasonDetail: "Valves fail pressure test at 80% of rated capacity. All 25 units are non-functional.",
    status: "rejected",
    requestDate: "2024-06-18",
    lastUpdated: "2024-06-21",
    hasPhotos: true,
    inspectionNotes: "Physical inspection showed no defects. Customer provided incorrect test conditions. Return rejected per policy.",
    priority: "low",
  },
  {
    id: "RET-2024-006",
    orderId: "ORD-1720",
    customerId: "CUST-390",
    customerName: "Vertex Engineering",
    customerEmail: "procurement@vertexeng.com",
    product: "Pneumatic Cylinder - 80mm Bore",
    sku: "PC-80MM-DBL",
    quantity: 5,
    unitPrice: 220,
    totalValue: 1100,
    reason: "changed_mind",
    reasonDetail: "Project scope changed. Items no longer needed.",
    status: "completed",
    requestDate: "2024-06-15",
    lastUpdated: "2024-06-19",
    hasPhotos: false,
    refundAmount: 990,
    priority: "low",
  },
];

const mockReplacements = [
  {
    id: "REP-2024-001",
    returnId: "RET-2024-002",
    orderId: "ORD-1745",
    customerId: "CUST-228",
    customerName: "Apex Manufacturing",
    product: "Hydraulic Press - 50 Ton",
    sku: "HP-50T-PRO",
    quantity: 1,
    status: "requested",
    requestDate: "2024-06-22",
    lastUpdated: "2024-06-22",
    notes: "Customer prefers replacement over refund. Expedited delivery requested.",
    priority: "high",
  },
  {
    id: "REP-2024-002",
    returnId: "RET-2024-003",
    orderId: "ORD-1801",
    customerId: "CUST-315",
    customerName: "Global Fabricators",
    product: "CNC Milling Bits - Set of 50 (Imperial)",
    sku: "CNC-MB-50SET-IMP",
    quantity: 3,
    status: "processing",
    requestDate: "2024-06-22",
    lastUpdated: "2024-06-23",
    notes: "Replacement with correct imperial sizing. Stock available.",
    priority: "medium",
  },
  {
    id: "REP-2024-003",
    returnId: "RET-2024-001",
    orderId: "ORD-1892",
    customerId: "CUST-441",
    customerName: "Meridian Logistics",
    product: "Industrial Conveyor Belt - 20m (Grade A)",
    sku: "CB-20M-IND-GA",
    quantity: 2,
    status: "approved",
    requestDate: "2024-06-23",
    lastUpdated: "2024-06-24",
    notes: "Upgrading to Grade A material at same price. Quality team to inspect before dispatch.",
    priority: "high",
  },
  {
    id: "REP-2024-004",
    returnId: "RET-2024-006",
    orderId: "ORD-1720",
    customerId: "CUST-390",
    customerName: "Vertex Engineering",
    product: "Pneumatic Cylinder - 100mm Bore",
    sku: "PC-100MM-DBL",
    quantity: 3,
    status: "shipped",
    requestDate: "2024-06-17",
    lastUpdated: "2024-06-20",
    trackingNumber: "FX-928374651",
    estimatedDelivery: "2024-06-26",
    notes: "Customer changed to larger bore size.",
    priority: "low",
  },
];

const reasonLabels = {
  defective: "Defective Product",
  wrong_item: "Wrong Item Sent",
  not_as_described: "Not As Described",
  damaged_shipping: "Damaged in Shipping",
  changed_mind: "Changed Mind",
  other: "Other",
};

const statusConfig = {
  pending_review: { label: "Pending Review", color: "bg-yellow-100 text-yellow-800", variant: "outline" },
  approved: { label: "Approved", color: "bg-green-100 text-green-800", variant: "default" },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-800", variant: "destructive" },
  inspection: { label: "Under Inspection", color: "bg-blue-100 text-blue-800", variant: "secondary" },
  completed: { label: "Completed", color: "bg-gray-100 text-gray-800", variant: "secondary" },
  refund_issued: { label: "Refund Issued", color: "bg-purple-100 text-purple-800", variant: "default" },
};

const replacementStatusConfig = {
  requested: { label: "Requested", color: "bg-yellow-100 text-yellow-800" },
  approved: { label: "Approved", color: "bg-green-100 text-green-800" },
  processing: { label: "Processing", color: "bg-blue-100 text-blue-800" },
  shipped: { label: "Shipped", color: "bg-indigo-100 text-indigo-800" },
  delivered: { label: "Delivered", color: "bg-gray-100 text-gray-800" },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-800" },
};

const normalizeReturn = (item) => {
  const statusMap = {
    RETURN_REQUESTED: "pending_review",
    RETURN_APPROVED: "approved",
    RETURN_REJECTED: "rejected",
    RETURN_RECEIVED_AT_HUB: "inspection",
    INSPECTION_PASSED: "approved",
    INSPECTION_FAILED: "rejected",
    DISPUTE_HOLD: "inspection",
    PICKUP_SCHEDULED: "approved",
  };

  const normalizedStatus = statusMap[item.status] || item.status || "pending_review";
  const requestedQty = item?.items?.reduce((sum, current) => sum + Number(current.requested_qty || 0), 0) || 0;
  const totalValue = Number(item.total_amount || item.refund_amount || 0);

  return {
    id: item.return_number || item.id || "",
    orderId: item.order_id?.order_number || item.order_id?.salesorder_number || item.order_id || "",
    customerId: item.customer_id?._id || item.customer_id || "",
    customerName: item.customer_name || item.customer_id?.name || "Customer",
    customerEmail: item.customer_email || "",
    product: item.product_name || item.items?.[0]?.item_name || "Return item",
    sku: item.sku || item.items?.[0]?.sku || "",
    quantity: requestedQty || item.items?.[0]?.requested_qty || 1,
    unitPrice: item.unit_price || 0,
    totalValue: totalValue || requestedQty * (item.unit_price || 0),
    reason: item.reason_code || "other",
    reasonDetail: item.reason_detail || item.rejection_reason || "No details provided",
    status: normalizedStatus,
    requestDate: item.requested_at ? new Date(item.requested_at).toISOString().split("T")[0] : "",
    lastUpdated: item.updated_at ? new Date(item.updated_at).toISOString().split("T")[0] : item.requestDate,
    hasPhotos: Boolean(item.media?.length),
    inspectionNotes: item.inspection?.notes || item.notes || "",
    refundAmount: item.refund_amount || null,
    priority: item.priority || "medium",
    raw: item,
  };
};

const normalizeReplacement = (item) => {
  const statusMap = {
    REPLACEMENT_REQUESTED: "requested",
    REPLACEMENT_APPROVED: "approved",
    REPLACEMENT_REJECTED: "rejected",
    REPLACEMENT_PICKUP_SCHEDULED: "processing",
    REPLACEMENT_RECEIVED_AT_HUB: "processing",
    INSPECTION_PASSED: "processing",
    REPLACEMENT_DISPATCHED: "shipped",
    DELIVERED: "delivered",
  };

  const normalizedStatus = statusMap[item.status] || item.status || "requested";

  return {
    id: item.replacement_number || item.id || "",
    returnId: item.return_number || item.return_id || "",
    orderId: item.order_id?.order_number || item.order_id?.salesorder_number || item.order_id || "",
    customerId: item.customer_id?._id || item.customer_id || "",
    customerName: item.customer_name || item.customer_id?.name || "Customer",
    product: item.product_name || item.items?.item_name || "Replacement item",
    sku: item.sku || item.items?.sku || "",
    quantity: Number(item.items?.requested_qty || 1),
    status: normalizedStatus,
    requestDate: item.requested_at ? new Date(item.requested_at).toISOString().split("T")[0] : "",
    lastUpdated: item.updated_at ? new Date(item.updated_at).toISOString().split("T")[0] : item.requestDate,
    notes: item.notes || item.reason_detail || "",
    priority: item.priority || "medium",
    trackingNumber: item.awb_number || item.tracking_number || "",
    estimatedDelivery: item.estimated_delivery || "",
    raw: item,
  };
};

function ReturnDetailPanel({ item, onClose, onStatusChange }) {
  const [notes, setNotes] = useState(item.inspectionNotes || "");
  const [refundAmount, setRefundAmount] = useState(
    item.refundAmount?.toString() || item.totalValue.toString()
  );

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4 py-6">
      <div className="my-auto flex w-full max-w-[720px] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl" style={{ maxHeight: "calc(100vh - 3rem)" }}>
        <div className="flex flex-shrink-0 items-center justify-between border-b p-5">
          <div>
            <div className="flex items-center gap-3">
              <h3>{item.id}</h3>
              <span
                className={`text-xs font-medium px-2.5 py-1 rounded-full ${statusConfig[item.status].color}`}
              >
                {statusConfig[item.status].label}
              </span>
            </div>
            <p className="text-muted-foreground mt-1">
              Order {item.orderId} · {item.customerName}
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="space-y-4 p-5">
            {/* Customer & Product */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Card className="h-full p-4 space-y-2">
                <div className="flex items-center gap-2 text-muted-foreground mb-2">
                  <User className="w-4 h-4" />
                  <span className="text-sm font-medium">Customer</span>
                </div>
                <p className="font-medium">{item.customerName}</p>
                <p className="text-sm text-muted-foreground">{item.customerEmail}</p>
                <p className="text-xs text-muted-foreground">ID: {item.customerId}</p>
              </Card>
              <Card className="p-4 space-y-2">
                <div className="flex items-center gap-2 text-muted-foreground mb-2">
                  <Package className="w-4 h-4" />
                  <span className="text-sm font-medium">Product</span>
                </div>
                <p className="font-medium">{item.product}</p>
                <p className="text-sm text-muted-foreground">SKU: {item.sku}</p>
                <p className="text-sm">
                  Qty: {item.quantity} × ${item.unitPrice.toLocaleString()} ={" "}
                  <span className="font-semibold">${item.totalValue.toLocaleString()}</span>
                </p>
              </Card>
            </div>

            {/* Return Reason */}
            <Card className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-3">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm font-medium">Return Reason</span>
                <Badge variant="outline" className="ml-auto text-xs">
                  {reasonLabels[item.reason]}
                </Badge>
              </div>
              <p className="text-sm">{item.reasonDetail}</p>
              {item.hasPhotos && (
                <div className="flex items-center gap-2 mt-3 text-blue-600">
                  <Camera className="w-4 h-4" />
                  <span className="text-sm">Customer-submitted photos available</span>
                </div>
              )}
            </Card>

            {/* Timeline */}
            <Card className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-3">
                <Calendar className="w-4 h-4" />
                <span className="text-sm font-medium">Timeline</span>
              </div>
              <div className="flex flex-wrap gap-6">
                <div>
                  <p className="text-xs text-muted-foreground">Requested</p>
                  <p className="text-sm font-medium">{item.requestDate}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Last Updated</p>
                  <p className="text-sm font-medium">{item.lastUpdated}</p>
                </div>
                {item.trackingNumber && (
                  <div>
                    <p className="text-xs text-muted-foreground">Return Tracking</p>
                    <p className="text-sm font-medium font-mono">{item.trackingNumber}</p>
                  </div>
                )}
              </div>
            </Card>

            {/* Inspection Notes */}
            <Card className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-3">
                <FileText className="w-4 h-4" />
                <span className="text-sm font-medium">Inspection / Admin Notes</span>
              </div>
              <textarea
                className="w-full border rounded-lg p-3 text-sm resize-none bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                rows={3}
                placeholder="Add inspection notes or comments..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Card>

            {/* Refund Amount (for approve) */}
            {(item.status === "pending_review" || item.status === "inspection") && (
              <Card className="p-4">
                <div className="flex items-center gap-2 text-muted-foreground mb-3">
                  <DollarSign className="w-4 h-4" />
                  <span className="text-sm font-medium">Refund Amount</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">$</span>
                  <Input
                    type="number"
                    value={refundAmount}
                    onChange={(e) => setRefundAmount(e.target.value)}
                    className="w-40"
                  />
                  <span className="text-sm text-muted-foreground">
                    (max ${item.totalValue.toLocaleString()})
                  </span>
                </div>
              </Card>
            )}
          </div>
        </div>

        {/* Action Footer */}
        <div className="flex-shrink-0 border-t bg-muted/30 p-5">
          {item.status === "pending_review" && (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-2">
                <Button
                  className="flex-1 min-w-[180px] bg-green-600 hover:bg-green-700"
                  onClick={() => { onStatusChange(item.id, "approved", notes); onClose(); }}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve Return
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1 min-w-[180px] bg-red-600 hover:bg-red-700"
                  onClick={() => { onStatusChange(item.id, "rejected", notes); onClose(); }}
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject Return
                </Button>
              </div>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => { onStatusChange(item.id, "inspection", notes); onClose(); }}
              >
                <Eye className="w-4 h-4 mr-2" />
                Send for Inspection First
              </Button>
            </div>
          )}

          {item.status === "inspection" && (
            <div className="flex flex-wrap gap-2">
              <Button
                className="flex-1 min-w-[180px] bg-green-600 hover:bg-green-700"
                onClick={() => { onStatusChange(item.id, "approved", notes); onClose(); }}
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Approve After Inspection
              </Button>
              <Button
                variant="destructive"
                className="flex-1"
                onClick={() => { onStatusChange(item.id, "rejected", notes); onClose(); }}
              >
                <XCircle className="w-4 h-4 mr-2" />
                Reject
              </Button>
            </div>
          )}

          {item.status === "approved" && (
            <div className="flex flex-wrap gap-2">
              <Button
                className="flex-1 min-w-[180px] bg-purple-600 hover:bg-purple-700"
                onClick={() => { onStatusChange(item.id, "refund_issued", notes); onClose(); }}
              >
                <DollarSign className="w-4 h-4 mr-2" />
                Issue Refund (${parseFloat(refundAmount).toLocaleString()})
              </Button>
              <Button
                variant="outline"
                className="flex-1 min-w-[180px]"
                onClick={() => { onStatusChange(item.id, "completed", notes); onClose(); }}
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Mark Completed
              </Button>
            </div>
          )}

          {(item.status === "completed" || item.status === "refund_issued" || item.status === "rejected") && (
            <div className="text-center text-sm text-muted-foreground py-1">
              This return has been resolved. No further actions required.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ReplacementDetailPanel({ item, onClose, onStatusChange }) {
  const [notes, setNotes] = useState(item.notes || "");
  const [tracking, setTracking] = useState(item.trackingNumber || "");

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4 py-6">
      <div className="my-auto flex w-full max-w-[680px] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl" style={{ maxHeight: "calc(100vh - 3rem)" }}>
        <div className="flex flex-shrink-0 items-center justify-between border-b p-5">
          <div>
            <div className="flex items-center gap-3">
              <h3>{item.id}</h3>
              <span
                className={`text-xs font-medium px-2.5 py-1 rounded-full ${replacementStatusConfig[item.status].color}`}
              >
                {replacementStatusConfig[item.status].label}
              </span>
            </div>
            <p className="text-muted-foreground mt-1">
              Return {item.returnId} · {item.customerName}
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="space-y-4 p-5">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Card className="h-full p-4 space-y-1">
                <p className="text-xs text-muted-foreground">Customer</p>
                <p className="font-medium">{item.customerName}</p>
                <p className="text-xs text-muted-foreground">ID: {item.customerId}</p>
              </Card>
              <Card className="h-full p-4 space-y-1">
                <p className="text-xs text-muted-foreground">Replacement Product</p>
                <p className="font-medium">{item.product}</p>
                <p className="text-xs text-muted-foreground">
                  SKU: {item.sku} · Qty: {item.quantity}
                </p>
              </Card>
            </div>

            <Card className="p-4">
              <p className="text-xs text-muted-foreground mb-2">Notes</p>
              <textarea
                className="w-full border rounded-lg p-3 text-sm resize-none bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add notes..."
              />
            </Card>

            {(item.status === "approved" || item.status === "processing" || item.status === "shipped") && (
              <Card className="p-4">
                <div className="flex items-center gap-2 text-muted-foreground mb-3">
                  <Truck className="w-4 h-4" />
                  <span className="text-sm font-medium">Shipping Information</span>
                </div>
                <div className="space-y-2">
                  <label className="text-xs text-muted-foreground">Tracking Number</label>
                  <Input
                    value={tracking}
                    onChange={(e) => setTracking(e.target.value)}
                    placeholder="Enter tracking number..."
                  />
                  {item.estimatedDelivery && (
                    <p className="text-xs text-muted-foreground">
                      Estimated delivery: {item.estimatedDelivery}
                    </p>
                  )}
                </div>
              </Card>
            )}
          </div>
        </div>

        <div className="flex-shrink-0 border-t bg-muted/30 p-5">
          {item.status === "requested" && (
            <div className="flex flex-wrap gap-3">
              <Button
                className="flex-1 min-w-[180px] bg-green-600 hover:bg-green-700"
                onClick={() => { onStatusChange(item.id, "approved", notes); onClose(); }}
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Approve Replacement
              </Button>
              <Button
                variant="destructive"
                className="flex-1 bg-red-600 hover:bg-red-700"
                onClick={() => { onStatusChange(item.id, "rejected", notes); onClose(); }}
              >
                <XCircle className="w-4 h-4 mr-2" />
                Reject
              </Button>
            </div>
          )}

          {item.status === "approved" && (
            <Button
              className="w-full bg-blue-600 hover:bg-blue-700"
              onClick={() => { onStatusChange(item.id, "processing", notes); onClose(); }}
            >
              <Package className="w-4 h-4 mr-2" />
              Start Processing
            </Button>
          )}

          {item.status === "processing" && (
            <Button
              className="w-full bg-indigo-600 hover:bg-indigo-700"
              onClick={() => { onStatusChange(item.id, "shipped", notes); onClose(); }}
            >
              <Truck className="w-4 h-4 mr-2" />
              Mark as Shipped
            </Button>
          )}

          {item.status === "shipped" && (
            <Button
              className="w-full bg-green-600 hover:bg-green-700"
              onClick={() => { onStatusChange(item.id, "delivered", notes); onClose(); }}
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Confirm Delivered
            </Button>
          )}

          {(item.status === "delivered" || item.status === "rejected") && (
            <div className="text-center text-sm text-muted-foreground">
              Replacement is resolved. No further actions required.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ReturnRow({ item, onSelect, onQuickAction }) {
  const [expanded, setExpanded] = useState(false);
  const isPending = item.status === "pending_review" || item.status === "inspection";

  return (
    <div className="border rounded-lg overflow-hidden">
      <div className="flex items-center gap-4 p-4 hover:bg-accent/30 transition-colors">
        <div
          className={`w-2 h-2 rounded-full flex-shrink-0 ${
            item.priority === "high"
              ? "bg-red-500"
              : item.priority === "medium"
              ? "bg-yellow-500"
              : "bg-gray-400"
          }`}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <span className="font-mono text-sm font-semibold">{item.id}</span>
            <span className="text-muted-foreground">·</span>
            <span className="text-sm text-muted-foreground">{item.orderId}</span>
            <span
              className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusConfig[item.status].color}`}
            >
              {statusConfig[item.status].label}
            </span>
          </div>
          <p className="text-sm font-medium mt-0.5 truncate">{item.product}</p>
          <p className="text-xs text-muted-foreground">
            {item.customerName} · {reasonLabels[item.reason]}
          </p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="font-semibold">${item.totalValue.toLocaleString()}</p>
          <p className="text-xs text-muted-foreground">{item.requestDate}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {isPending && (
            <>
              <Button
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white h-8 px-3"
                onClick={() => onQuickAction(item.id, "approved")}
              >
                <CheckCircle className="w-3.5 h-3.5 mr-1" />
                Approve
              </Button>
              <Button
                size="sm"
                variant="destructive"
                className="bg-red-600 hover:bg-red-700 text-white h-8 px-3"
                onClick={() => onQuickAction(item.id, "rejected")}
              >
                <XCircle className="w-3.5 h-3.5 mr-1" />
                Reject
              </Button>
            </>
          )}
          <Button size="sm" variant="outline" onClick={() => onSelect(item)}>
            Details
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 pt-0 border-t bg-muted/20">
          <p className="text-sm mt-3 text-muted-foreground leading-relaxed">
            <span className="font-medium text-foreground">Reason: </span>
            {item.reasonDetail}
          </p>
          {item.inspectionNotes && (
            <p className="text-sm mt-2 text-muted-foreground leading-relaxed">
              <span className="font-medium text-foreground">Inspection Notes: </span>
              {item.inspectionNotes}
            </p>
          )}
          <div className="flex items-center gap-4 mt-3">
            {item.hasPhotos && (
              <span className="flex items-center gap-1 text-xs text-blue-600">
                <Camera className="w-3 h-3" />
                Photos attached
              </span>
            )}
            {item.refundAmount && (
              <span className="flex items-center gap-1 text-xs text-green-600">
                <DollarSign className="w-3 h-3" />
                Refund: ${item.refundAmount.toLocaleString()}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ReplacementRow({ item, onSelect, onQuickAction }) {
  return (
    <div className="flex items-center gap-4 p-4 border rounded-lg hover:bg-accent/30 transition-colors">
      <div
        className={`w-2 h-2 rounded-full flex-shrink-0 ${
          item.priority === "high"
            ? "bg-red-500"
            : item.priority === "medium"
            ? "bg-yellow-500"
            : "bg-gray-400"
        }`}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3">
          <span className="font-mono text-sm font-semibold">{item.id}</span>
          <span className="text-muted-foreground">·</span>
          <span className="text-xs text-muted-foreground">{item.returnId}</span>
          <span
            className={`text-xs font-medium px-2 py-0.5 rounded-full ${replacementStatusConfig[item.status].color}`}
          >
            {replacementStatusConfig[item.status].label}
          </span>
        </div>
        <p className="text-sm font-medium mt-0.5 truncate">{item.product}</p>
        <p className="text-xs text-muted-foreground">
          {item.customerName} · Qty: {item.quantity}
        </p>
      </div>
      <div className="text-right flex-shrink-0">
        {item.trackingNumber ? (
          <div className="flex items-center gap-1 text-xs text-indigo-600">
            <Truck className="w-3 h-3" />
            <span className="font-mono">{item.trackingNumber}</span>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">{item.requestDate}</p>
        )}
        {item.estimatedDelivery && (
          <p className="text-xs text-muted-foreground mt-0.5">
            ETA: {item.estimatedDelivery}
          </p>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {item.status === "requested" && (
          <>
            <Button
              size="sm"
              className="bg-green-600 hover:bg-green-700 text-white h-8 px-3"
              onClick={() => onQuickAction(item.id, "approved")}
            >
              <CheckCircle className="w-3.5 h-3.5 mr-1" />
              Approve
            </Button>
            <Button
              size="sm"
              variant="destructive"
              className="bg-red-600 hover:bg-red-700 text-white h-8 px-3"
              onClick={() => onQuickAction(item.id, "rejected")}
            >
              <XCircle className="w-3.5 h-3.5 mr-1" />
              Reject
            </Button>
          </>
        )}
        <Button size="sm" variant="outline" onClick={() => onSelect(item)}>
          Details
        </Button>
      </div>
    </div>
  );
}


/* function CreateRequestModal({ type, onClose }) {
  const [form, setForm] = useState({
    orderId: "",
    customer: "",
    product: "",
    quantity: 1,
    reason: "",
    notes: "",
  });

  const handleChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSubmit = () => {
    console.log(type, form);

    toast.success(
      `${type === "return" ? "Return" : "Replacement"} created successfully`
    );

    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-white rounded-xl w-full max-w-lg p-6 space-y-5">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-semibold">
            Create {type === "return" ? "Return" : "Replacement"}
          </h2>

          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        <Input
          placeholder="Order ID"
          value={form.orderId}
          onChange={(e) => handleChange("orderId", e.target.value)}
        />

        <Input
          placeholder="Customer"
          value={form.customer}
          onChange={(e) => handleChange("customer", e.target.value)}
        />

        <Input
          placeholder="Product"
          value={form.product}
          onChange={(e) => handleChange("product", e.target.value)}
        />

        <Input
          type="number"
          placeholder="Quantity"
          value={form.quantity}
          onChange={(e) => handleChange("quantity", e.target.value)}
        />

        <textarea
          className="w-full border rounded-md p-3"
          rows={3}
          placeholder="Reason / Notes"
          value={form.reason}
          onChange={(e) => handleChange("reason", e.target.value)}
        />

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>

          <Button onClick={handleSubmit}>
            Create {type === "return" ? "Return" : "Replacement"}
          </Button>
        </div>
      </div>
    </div>
  );
} */

export function ReturnsManager() {
  const [returns, setReturns] = useState([]);
  const [replacements, setReplacements] = useState([]);
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [selectedReplacement, setSelectedReplacement] = useState(null);
  const [returnsSearch, setReturnsSearch] = useState("");
  const [returnsStatusFilter, setReturnsStatusFilter] = useState([]);
  const [replacementsSearch, setReplacementsSearch] = useState("");
  const [replacementsStatusFilter, setReplacementsStatusFilter] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState("");
  const [returnsPage, setReturnsPage] = useState(1);
  const [replacementsPage, setReplacementsPage] = useState(1);
  const [showReturnsStatusDropdown, setShowReturnsStatusDropdown] = useState(false);
  const [showReplacementsStatusDropdown, setShowReplacementsStatusDropdown] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeTab, setActiveTab] = useState("returns");
  const [createType, setCreateType] = useState("return"); // return | replacement
  const backendBaseUrl = process.env.REACT_APP_FORE_BRICKBUCKET;
  const pageSize = 5;
  const returnStatusOptions = Object.keys(statusConfig);
  const replacementStatusOptions = Object.keys(replacementStatusConfig);
  const encryptedToken = sessionStorage.getItem('token');
  const token = decryptData(encryptedToken);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      setApiError("");
      const response = await fetch(`${backendBaseUrl}/returns/get-all-return-requests`, {
        headers: {
          "x-token": token,
          "Content-Type": "application/json",
        },
      });

      const result = await response.json();
      const payload = result?.data || {};
      setReturns((payload.returns || []).map(normalizeReturn));
      setReplacements((payload.replacements || []).map(normalizeReplacement));
    } catch (error) {
      console.error("Failed to load returns data", error);
      setApiError("Unable to load live returns data. Showing sample data.");
      setReturns(mockReturns.map(normalizeReturn));
      setReplacements(mockReplacements.map(normalizeReplacement));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const handleReturnStatusChange = async (id, status, notes) => {
    try {
      const endpoint = status === "rejected" ? "reject-return" : "approve-return";
      const response = await fetch(`${backendBaseUrl}/returns/${endpoint}`, {
        method: "POST",
        headers: {
          "x-token": token ,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(
          status === "rejected"
            ? { id: id.replace(/^RN-/, ""), rejection_reason: notes || "No reason provided" }
            : { id: id.replace(/^RN-/, ""), pickup_charge_confirmed: true }
        ),
      });

      if (!response.ok) {
        throw new Error("Unable to update return status");
      }

      setReturns((prev) =>
        prev.map((r) =>
          r.id === id
            ? { ...r, status, inspectionNotes: notes || r.inspectionNotes, lastUpdated: new Date().toISOString().split("T")[0] }
            : r
        )
      );

      const action =
        status === "approved"
          ? "approved"
          : status === "rejected"
          ? "rejected"
          : status === "inspection"
          ? "sent for inspection"
          : status === "refund_issued"
          ? "refund issued for"
          : "updated";
      toast.success(`Return ${id} ${action} successfully`);
    } catch (error) {
      console.error("Failed to update return", error);
      toast.success(`Return ${id} updated locally`);
    }
  };

  const handleReplacementStatusChange = async (id, status, notes) => {
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      const endpoint = status === "rejected" ? "reject-replacement" : "approve-replacement";
      const response = await fetch(`${backendBaseUrl}/returns/${endpoint}`, {
        method: "POST",
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id: id.replace(/^RP-/, ""), rejection_reason: notes || "No reason provided" }),
      });

      if (!response.ok) {
        throw new Error("Unable to update replacement status");
      }

      setReplacements((prev) =>
        prev.map((r) =>
          r.id === id
            ? { ...r, status, notes: notes || r.notes, lastUpdated: new Date().toISOString().split("T")[0] }
            : r
        )
      );
      toast.success(`Replacement ${id} status updated to ${replacementStatusConfig[status].label}`);
    } catch (error) {
      console.error("Failed to update replacement", error);
      toast.success(`Replacement ${id} updated locally`);
    }
  };

  const toggleReturnStatus = (status) => {
    setReturnsStatusFilter((prev) => {
      const next = prev.includes(status) ? prev.filter((item) => item !== status) : [...prev, status];
      return next;
    });
    setReturnsPage(1);
  };

  const toggleReplacementStatus = (status) => {
    setReplacementsStatusFilter((prev) => {
      const next = prev.includes(status) ? prev.filter((item) => item !== status) : [...prev, status];
      return next;
    });
    setReplacementsPage(1);
  };

  const toggleReturnSelectAll = () => {
    if (returnsStatusFilter.length === returnStatusOptions.length) {
      setReturnsStatusFilter([]);
    } else {
      setReturnsStatusFilter(returnStatusOptions);
    }
    setReturnsPage(1);
  };

  const toggleReplacementSelectAll = () => {
    if (replacementsStatusFilter.length === replacementStatusOptions.length) {
      setReplacementsStatusFilter([]);
    } else {
      setReplacementsStatusFilter(replacementStatusOptions);
    }
    setReplacementsPage(1);
  };

  const filteredReturns = returns.filter((r) => {
    const matchesSearch =
      returnsSearch === "" ||
      r.id.toLowerCase().includes(returnsSearch.toLowerCase()) ||
      r.customerName.toLowerCase().includes(returnsSearch.toLowerCase()) ||
      r.product.toLowerCase().includes(returnsSearch.toLowerCase()) ||
      r.orderId.toLowerCase().includes(returnsSearch.toLowerCase());
    const matchesStatus = returnsStatusFilter.length === 0 || returnsStatusFilter.includes(r.status);
    return matchesSearch && matchesStatus;
  });

  const filteredReplacements = replacements.filter((r) => {
    const searchValue = replacementsSearch.toLowerCase();
    const matchesSearch =
      searchValue === "" ||
      r.id.toLowerCase().includes(searchValue) ||
      r.customerName.toLowerCase().includes(searchValue) ||
      r.product.toLowerCase().includes(searchValue) ||
      r.orderId.toLowerCase().includes(searchValue) ||
      r.returnId.toLowerCase().includes(searchValue);
    const matchesStatus = replacementsStatusFilter.length === 0 || replacementsStatusFilter.includes(r.status);
    return matchesSearch && matchesStatus;
  });

  const sortedReturns = [...filteredReturns].sort((a, b) => {
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    return priorityOrder[a.priority] - priorityOrder[b.priority];
  });

  const sortedReplacements = [...filteredReplacements].sort((a, b) => {
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    return priorityOrder[a.priority] - priorityOrder[b.priority];
  });

  const totalReturnPages = Math.max(1, Math.ceil(sortedReturns.length / pageSize));
  const totalReplacementPages = Math.max(1, Math.ceil(sortedReplacements.length / pageSize));
  const safeReturnsPage = Math.min(returnsPage, totalReturnPages);
  const safeReplacementsPage = Math.min(replacementsPage, totalReplacementPages);
  const pagedReturns = sortedReturns.slice((safeReturnsPage - 1) * pageSize, safeReturnsPage * pageSize);
  const pagedReplacements = sortedReplacements.slice((safeReplacementsPage - 1) * pageSize, safeReplacementsPage * pageSize);

  const pendingCount = returns.filter(
    (r) => r.status === "pending_review" || r.status === "inspection"
  ).length;
  const replacementPendingCount = replacements.filter(
    (r) => r.status === "requested" || r.status === "approved"
  ).length;

  return (
    <div className="space-y-6">
      {apiError && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
          {apiError}
        </div>
      )}

      {/* Summary Cards */}
{/*       <div className="grid grid-cols-4 gap-4">
        {[
          {
            label: "Pending Review",
            count: returns.filter((r) => r.status === "pending_review").length,
            icon: Clock,
            color: "text-yellow-600",
            bg: "bg-yellow-50",
          },
          {
            label: "Under Inspection",
            count: returns.filter((r) => r.status === "inspection").length,
            icon: Eye,
            color: "text-blue-600",
            bg: "bg-blue-50",
          },
          {
            label: "Approved",
            count: returns.filter((r) => r.status === "approved").length,
            icon: CheckCircle,
            color: "text-green-600",
            bg: "bg-green-50",
          },
          {
            label: "Replacements Active",
            count: replacements.filter(
              (r) => r.status !== "delivered" && r.status !== "rejected"
            ).length,
            icon: RefreshCw,
            color: "text-indigo-600",
            bg: "bg-indigo-50",
          },
        ].map((stat) => (
          <Card key={stat.label} className="p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${stat.bg}`}>
                <stat.icon className={`w-5 h-5 ${stat.color}`} />
              </div>
              <div>
                <p className="text-2xl font-bold">{stat.count}</p>
                <p className="text-xs text-muted-foreground">{stat.label}</p>
              </div>
            </div>
          </Card>
        ))}
      </div> */}

      {/* Tabs: Returns | Replacements */}
          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
          >
         {/* <TabsList className="bg-muted/70 p-1">
          <TabsTrigger value="returns" className="flex items-center gap-2 rounded-md border border-transparent px-3 py-2 data-[state=active]:bg-gray-100 data-[state=active]:text-black data-[state=active]:shadow-sm data-[state=active]:border-gray-100">
            <RotateCcw className="w-4 h-4" />
            Returns
            {pendingCount > 0 && (
              <Badge variant="destructive" className="ml-1 text-xs px-1.5">
                {pendingCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="replacements" className="flex items-center gap-2 rounded-md border border-transparent px-3 py-2 data-[state=active]:bg-gray-100 data-[state=active]:text-black data-[state=active]:shadow-sm data-[state=active]:border-gray-100">
            <RefreshCw className="w-4 h-4" />
            Replacements
            {replacementPendingCount > 0 && (
              <Badge variant="destructive" className="ml-1 text-xs px-1.5">
                {replacementPendingCount}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>*/}
        <div className="flex items-center justify-between">
          <TabsList className="bg-muted/70 p-1">
            <TabsTrigger value="returns" className="
                rounded-md
                px-4
                data-[state=active]:bg-gray-100
                data-[state=active]:text-black
                data-[state=active]:shadow-sm"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Returns

              {pendingCount > 0 && (
                <Badge variant="destructive" className="ml-1">
                  {pendingCount}
                </Badge>
              )}
            </TabsTrigger>

            <TabsTrigger value="replacements" className="
                rounded-md
                px-4
                data-[state=active]:bg-gray-100
                data-[state=active]:text-black
                data-[state=active]:shadow-sm"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Replacements

              {replacementPendingCount > 0 && (
                <Badge variant="destructive" className="ml-1">
                  {replacementPendingCount}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>
        <div className="group relative">
          <Button
            size="icon"
            variant="default"
            className="mr-4 h-9 w-9 bg-[#f7525a] text-white hover:bg-[#e5454d]"
            onClick={() => {
              setCreateType(
                activeTab === "returns" ? "return" : "replacement"
              );
              setShowCreateModal(true);
            }}
          >
            <Plus className="h-4 w-4" />
          </Button>

          <div
            className="
              absolute
              right-0
              top-full
              mt-2
              z-50
              whitespace-nowrap
              rounded-md
              bg-black
              px-2
              py-1
              text-xs
              text-white
              opacity-0
              transition-opacity
              duration-200
              pointer-events-none
              group-hover:opacity-100
            "
          >
            {activeTab === "returns"
              ? "Create Return"
              : "Create Replacement"}
          </div>
        </div>
        </div>

        {/* Returns Tab */}
        <TabsContent value="returns" className="space-y-4 mt-4">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search returns or shipments"
                value={returnsSearch}
                onChange={(e) => {
                  setReturnsSearch(e.target.value);
                  setReturnsPage(1);
                }}
                className="w-full pl-10 pr-4 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="relative status-dropdown">
              <Filter className="absolute left-3 top-3 w-4 h-4 text-gray-400 pointer-events-none" />
              <button
                type="button"
                onClick={() => {
                  setShowReturnsStatusDropdown((prev) => !prev);
                  setShowReplacementsStatusDropdown(false);
                }}
                className="pl-10 pr-8 py-2 border rounded-md text-sm bg-white focus:outline-none min-w-[220px] text-left"
              >
                {returnsStatusFilter.length === 0 ? "Return Status" : `${returnsStatusFilter.length} Selected`}
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              </button>
              {showReturnsStatusDropdown && (
                <div className="absolute z-20 mt-1 w-full bg-white border rounded-md shadow-lg">
                  <div className="flex items-center justify-between px-3 py-2 border-b">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={returnsStatusFilter.length === returnStatusOptions.length}
                        onChange={toggleReturnSelectAll}
                      />
                      <span className="text-sm font-medium">All Status</span>
                    </label>
                    <button
                      onClick={() => {
                        setReturnsStatusFilter([]);
                        setReturnsPage(1);
                      }}
                      className="text-xs text-red-600 hover:underline"
                    >
                      Clear
                    </button>
                  </div>
                  <div className="max-h-60 overflow-y-auto">
                    {returnStatusOptions.map((status) => (
                      <label key={status} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm">
                        <input
                          type="checkbox"
                          checked={returnsStatusFilter.includes(status)}
                          onChange={() => toggleReturnStatus(status)}
                        />
                        <span>{statusConfig[status]?.label || status}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Return List */}
          <div className="space-y-2">
            {loading ? (
              <div className="text-center py-12 text-muted-foreground">Loading returns...</div>
            ) : filteredReturns.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                No returns match your filter
              </div>
            ) : (
              <>
                {pagedReturns.map((item) => (
                  <ReturnRow
                    key={item.id}
                    item={item}
                    onSelect={setSelectedReturn}
                    onQuickAction={(id, status) => handleReturnStatusChange(id, status)}
                  />
                ))}
                {sortedReturns.length > pageSize && (
                  <div className="rounded-lg border bg-white">
                    <Pagination
                      currentPage={safeReturnsPage}
                      totalPages={totalReturnPages}
                      totalItems={sortedReturns.length}
                      itemsPerPage={pageSize}
                      onPageChange={(page) => setReturnsPage(page)}
                      onPreviousPage={() => setReturnsPage((page) => Math.max(1, page - 1))}
                      onNextPage={() => setReturnsPage((page) => Math.min(totalReturnPages, page + 1))}
                      showInfo={true}
                      itemName="returns"
                      className="border-0 shadow-none"
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </TabsContent>

        {/* Replacements Tab */}
        <TabsContent value="replacements" className="space-y-4 mt-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search replacements or shipments"
                value={replacementsSearch}
                onChange={(e) => {
                  setReplacementsSearch(e.target.value);
                  setReplacementsPage(1);
                }}
                className="w-full pl-10 pr-4 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="relative status-dropdown">
              <Filter className="absolute left-3 top-3 w-4 h-4 text-gray-400 pointer-events-none" />
              <button
                type="button"
                onClick={() => {
                  setShowReplacementsStatusDropdown((prev) => !prev);
                  setShowReturnsStatusDropdown(false);
                }}
                className="pl-10 pr-8 py-2 border rounded-md text-sm bg-white focus:outline-none min-w-[220px] text-left"
              >
                {replacementsStatusFilter.length === 0 ? "Replacement Status" : `${replacementsStatusFilter.length} Selected`}
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              </button>
              {showReplacementsStatusDropdown && (
                <div className="absolute z-20 mt-1 w-full bg-white border rounded-md shadow-lg">
                  <div className="flex items-center justify-between px-3 py-2 border-b">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={replacementsStatusFilter.length === replacementStatusOptions.length}
                        onChange={toggleReplacementSelectAll}
                      />
                      <span className="text-sm font-medium">All Status</span>
                    </label>
                    <button
                      onClick={() => {
                        setReplacementsStatusFilter([]);
                        setReplacementsPage(1);
                      }}
                      className="text-xs text-red-600 hover:underline"
                    >
                      Clear
                    </button>
                  </div>
                  <div className="max-h-60 overflow-y-auto">
                    {replacementStatusOptions.map((status) => (
                      <label key={status} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm">
                        <input
                          type="checkbox"
                          checked={replacementsStatusFilter.includes(status)}
                          onChange={() => toggleReplacementStatus(status)}
                        />
                        <span>{replacementStatusConfig[status]?.label || status}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="space-y-2">
            {loading ? (
              <div className="text-center py-12 text-muted-foreground">Loading replacements...</div>
            ) : (
              <>
                {pagedReplacements.map((item) => (
                  <ReplacementRow
                    key={item.id}
                    item={item}
                    onSelect={setSelectedReplacement}
                    onQuickAction={(id, status) => handleReplacementStatusChange(id, status)}
                  />
                ))}
                {sortedReplacements.length > pageSize && (
                  <div className="rounded-lg border bg-white">
                    <Pagination
                      currentPage={safeReplacementsPage}
                      totalPages={totalReplacementPages}
                      totalItems={sortedReplacements.length}
                      itemsPerPage={pageSize}
                      onPageChange={(page) => setReplacementsPage(page)}
                      onPreviousPage={() => setReplacementsPage((page) => Math.max(1, page - 1))}
                      onNextPage={() => setReplacementsPage((page) => Math.min(totalReplacementPages, page + 1))}
                      showInfo={true}
                      itemName="replacements"
                      className="border-0 shadow-none"
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {showCreateModal && (
        <CreateRequestModal
          type={createType}
          onClose={() => setShowCreateModal(false)}
        />
      )}

      {/* Detail Panels */}
      {selectedReturn && (
        <ReturnDetailPanel
          item={selectedReturn}
          onClose={() => setSelectedReturn(null)}
          onStatusChange={handleReturnStatusChange}
        />
      )}
      {selectedReplacement && (
        <ReplacementDetailPanel
          item={selectedReplacement}
          onClose={() => setSelectedReplacement(null)}
          onStatusChange={handleReplacementStatusChange}
        />
      )}
    </div>
  );
}
